import { Directive, OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { TicketAdminFilterKey, TicketSummaryDto } from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import { environment } from '@workspace/ng-config';
import { Observable } from 'rxjs';

export type TicketColumnKey =
  'zammadTicketId' | 'user' | 'subject' | 'status' | 'updatedAt' | 'createdAt' | 'priority' | 'tags';

@Directive()
export abstract class AbstractTicketsDataTableCoreComponent<T extends TicketSummaryDto>
  extends BaseDataTableComponent<T, TicketColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<TicketColumnKey>[]>([
    { key: 'zammadTicketId', label: 'ID Ticket', display: true },
    { key: 'user', label: 'Utilisateur', display: true },
    { key: 'subject', label: 'Sujet', display: true },
    { key: 'tags', label: 'Tags', display: true },
    { key: 'priority', label: 'Priorité', display: true },
    { key: 'status', label: 'Statut', display: true },
    { key: 'updatedAt', label: 'Date de modification', display: false },
    { key: 'createdAt', label: 'Date de création', display: true },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'visibility',
      onClick: (ticket: TicketSummaryDto) => this.navigateToTicket(ticket),
      disabled: false,
      color: 'primary',
    },
  ]);

  override filters = signal<DataHeaderFilter<TicketAdminFilterKey>[]>([
    {
      label: 'Priorité',
      column: TicketAdminFilterKey.Priority,
    },
    {
      label: 'Statut',
      column: TicketAdminFilterKey.Status,
    },
  ]);

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const status = params.getAll(TicketAdminFilterKey.Status);
      const priority = params.getAll(TicketAdminFilterKey.Priority);
      this.filterChanged({
        changed: true,
        filter: {
          status: status.length ? status : [],
          priority: priority.length ? priority : [],
        },
      });
    });
  }

  navigateToTicket(ticket: TicketSummaryDto): void {
    void this.router.navigate(['/tickets', ticket._id]);
  }

  openInZammad(ticket: TicketSummaryDto): void {
    const url = `${environment.apps.zammadUrl}/#ticket/zoom/${ticket.zammadTicketId}`;
    window.open(url, '_blank');
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;
  override getDisplayForEntry(key: TicketAdminFilterKey, rawValue: string): string {
    if (key === TicketAdminFilterKey.Priority) {
      if (rawValue === 'high') return 'Haute';
      if (rawValue === 'low') return 'Basse';
      if (rawValue === 'normal') return 'Normale';
    }
    if (key === TicketAdminFilterKey.Status) {
      if (rawValue === 'new') return 'Nouveau';
      if (rawValue === 'open') return 'Ouvert';
      if (rawValue === 'closed') return 'Fermé';
    }
    return rawValue;
  }
}
